/**
 * Collapse a concrete path into the ROUTE it belongs to.
 *
 * `/home/acme-civil/invoices/3f8c…/lines` -> `/home/:slug/invoices/:id/lines`
 *
 * Analytics needs this wherever an event is keyed by location. Without it
 * every record touched becomes its own "page" or "endpoint", and any
 * most-used / most-blocked report degenerates into a list of one-hit rows —
 * present, plausible and useless.
 *
 * Ids are REPLACED, not dropped: the position of a parameter is what
 * distinguishes one route from another, and `/invoices/:id/lines` is not
 * `/invoices/lines`.
 *
 * The account slug is collapsed too. It is the second segment of every
 * authenticated route, it is per-tenant, and leaving it in would split one
 * route into one row per customer — which is exactly backwards, since the
 * tenant already travels on the event as `accountId`.
 */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * The tenant slug in a `/home/<slug>/...` path, or `undefined` outside one.
 *
 * Found by POSITION relative to `home` for the same reason
 * {@link normalizeRoutePattern} is: `usePathname` includes a locale prefix on
 * non-default locales, so `/en/home/acme/...` puts the slug one segment later.
 *
 * Required — and it IS the slug, not just a segment after `home`: a third
 * segment must exist, or `/home/settings` (a personal-account route) would
 * report `settings` as a tenant.
 *
 * Exists because SESSION-DEDUPE KEYS need the tenant even though the emitted
 * event does not. `normalizeRoutePattern` collapses the slug on purpose — the
 * account rides the event separately — but a dedupe key built from the
 * collapsed pattern is shared across tenants, and the module-scoped `seen`
 * set survives a tenant switch. `filter.applied`, `search.performed` and
 * `control.blocked` were each swallowed for every account after the first
 * (BugBot, PR #6368); `feature.viewed` had the same bug and grew its own copy
 * of this logic, which this replaces.
 */
export function accountSlugFromPathname(pathname: string): string | undefined {
  const segments = pathname.split('/').filter(Boolean);
  const homeIndex = segments.indexOf('home');

  if (homeIndex === -1) return undefined;

  // No third segment means a personal-account route, which has no tenant.
  return segments[homeIndex + 2] ? segments[homeIndex + 1] : undefined;
}

export function normalizeRoutePattern(pathname: string): string {
  const segments = pathname.split('/');
  // Found by POSITION relative to `home`, not at a fixed index. `usePathname`
  // includes a locale prefix on non-default locales (`localePrefix:
  // 'as-needed'`), so `/en/home/acme/...` puts the tenant one segment later
  // than `/home/acme/...`. Assuming the unprefixed shape left the raw slug in
  // place for every non-English user, splitting one control's identity by
  // language AND by customer.
  const slugIndex = segments.indexOf('home') + 1;

  return segments
    .map((segment, index) => {
      // The tenant, which rides the event separately as `accountId`.
      if (index === slugIndex && slugIndex > 0) return ':slug';
      if (UUID.test(segment)) return ':id';
      if (/^\d+$/.test(segment)) return ':id';
      return segment;
    })
    .join('/');
}

/**
 * The same collapse for a full URL, for callers holding `request.url`.
 *
 * Returns `undefined` rather than guessing when the URL will not parse: its
 * callers run in `finally` blocks, where a throw would cost a response.
 */
export function normalizeUrlPattern(url: string): string | undefined {
  try {
    return normalizeRoutePattern(new URL(url).pathname);
  } catch {
    return undefined;
  }
}
